import { TitleCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree, FormField } from "@angular/forms/signals";
import { StrategyDspFunnel } from "@front/m19-api-client";
import { DspStrategyService } from "@front/m19-services";
import {
  IErrorComponent,
  IFormFieldComponent,
  IInputDirective,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ILabelComponent,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { DspFormService } from "@m19-board/dsp/dsp-create-strategy/dsp-form.service";
import { DspFunnelSelectorComponent } from "@m19-board/dsp/dsp-funnel/dsp-funnel-selector.component";

export interface DspCreateStrategySetupModel {
  name: string;
  agencyFee: number | null;
  dspFunnel: StrategyDspFunnel | null;
}

@Component({
  selector: "app-dsp-create-strategy-setup-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    TitleCasePipe,
    FormField,
    IFormFieldComponent,
    IInputDirective,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupSuffixDirective,
    ILabelComponent,
    IErrorComponent,
    DspFunnelSelectorComponent,
  ],
  template: `
    <div class="flex flex-col gap-6 py-8" *transloco="let t">
      <div class="flex flex-col gap-1">
        <h4>{{ t("dsp-create-strategy-page.setup_title") }}</h4>
        <p class="type-body text-muted-foreground m-0">{{ t("dsp-create-strategy-page.setup_description") }}</p>
      </div>

      <IFormField>
        <label iLabel>{{ t("common.name") }}</label>
        <input iInput [formField]="setup().name" size="sm" [placeholder]="t('common.name')" autofocus />
        <IError>{{ t("dsp-create-strategy-page.name_is_required") }}</IError>
      </IFormField>

      @if (allowAgencyFee()) {
        <IFormField>
          <div class="flex items-center gap-1">
            <label iLabel>{{ t("dsp.agency_fee") }}</label>
            <span class="type-label text-muted-foreground">- {{ t("common.optional") | titlecase }}</span>
          </div>
          <div iInputGroup>
            <input
              iInputGroupInput
              type="number"
              [formField]="setup().agencyFee"
              size="sm"
              [placeholder]="t('common.fee')"
            />
            <span class="text-muted-foreground" iInputGroupSuffix> % </span>
          </div>
          <IError>{{
            t("dsp-create-strategy-page.agency_fee_must_be_between", [AGENCY_FEE_MIN, AGENCY_FEE_MAX])
          }}</IError>
        </IFormField>
      }

      <div class="flex flex-col gap-2">
        <div class="type-label text-muted-foreground">{{ t("strategy-page.funnel") }}</div>
        <app-dsp-funnel-selector
          [selectedFunnel]="setup().dspFunnel().value() ?? undefined"
          (selectedFunnelChange)="selectFunnel($event)"
        />
      </div>
    </div>
  `,
})
export class DspCreateStrategySetupStepComponent {
  private readonly dspFormService = inject(DspFormService);

  readonly setup = input.required<FieldTree<DspCreateStrategySetupModel>>();

  readonly AGENCY_FEE_MIN = DspStrategyService.AGENCY_FEE_MIN;
  readonly AGENCY_FEE_MAX = DspStrategyService.AGENCY_FEE_MAX;
  readonly allowAgencyFee = toSignal(this.dspFormService.allowAgencyFee$, { initialValue: false });

  selectFunnel(funnel: StrategyDspFunnel | undefined) {
    this.setup().dspFunnel().markAsDirty();
    this.setup()
      .dspFunnel()
      .value.set(funnel ?? null);
  }
}
